
import { useState, useEffect } from 'react';
import { CVEItem } from '@/types/cve';
import { useCVEYearData, YEARS_TO_LOAD } from './cve/useCVEYearData';
import { useCVEFilter } from './cve/useCVEFilter';
import { useCVESearch } from './cve/useCVESearch';

export { YEARS_TO_LOAD } from './cve/useCVEYearData';

export function useCVEData() {
  const {
    cveData,
    loadingYears,
    error,
    activeYear,
    loadYearData,
    changeYear
  } = useCVEYearData();

  const {
    selectedSeverities,
    toggleSeverity,
    filterCVEItems
  } = useCVEFilter();

  const {
    searchQuery,
    isSearching,
    searchResults,
    searchLoading,
    setSearchQuery,
    clearSearch
  } = useCVESearch(loadYearData, cveData, YEARS_TO_LOAD);

  const [filteredCVEs, setFilteredCVEs] = useState<CVEItem[]>([]);
  const [sortOption, setSortOption] = useState<string>('date-desc');

  // Load active year data
  useEffect(() => {
    loadYearData(activeYear);
  }, [activeYear, loadYearData]);

  // Apply filters and sorting
  useEffect(() => {
    let result: CVEItem[] = [];

    if (isSearching) {
      // When searching, filter search results
      result = filterCVEItems(searchResults);
    } else if (cveData[activeYear]) {
      // Normal browsing mode - filter by severity for current year
      result = filterCVEItems(cveData[activeYear]);
    }

    // Apply Sorting
    result.sort((a, b) => {
      switch (sortOption) {
        case 'date-desc':
          return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
        case 'date-asc':
          return new Date(a.publishedDate).getTime() - new Date(b.publishedDate).getTime();
        case 'score-desc':
          return b.score - a.score;
        case 'score-asc':
          return a.score - b.score;
        default:
          return 0;
      }
    });

    setFilteredCVEs(result);
  }, [cveData, activeYear, selectedSeverities, isSearching, searchResults, filterCVEItems, sortOption]);

  return {
    cveData,
    loadingYears,
    error,
    activeYear,
    selectedSeverities,
    filteredCVEs,
    searchQuery,
    isSearching,
    searchLoading,
    sortOption,
    loadYearData,
    changeYear,
    toggleSeverity,
    setSearchQuery,
    clearSearch,
    setSortOption
  };
}
